import React, { useRef, useEffect, useContext, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import axios from 'axios';

import { EntryContext } from '../contexts/entryContext';
import API from '../API';
import DisplayContent from '../utils/DisplayContent';
import ErrorMessage from '../components/ErrorMessage';
import ERROR_MESSAGES from '../contants/errorConstants';

function useQuery() {
    const { search } = useLocation();
    return React.useMemo(() => new URLSearchParams(search), [search]);
}

const Home = () => {
    const navigate = useNavigate();
    const hasApiBeenCalled = useRef(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const { entry, setEntry, siteAssets, setSiteAssets, setCmsVersion, game, setGame, revealGames, setRevealGames, igrAuthToken, setIgrAuthToken, errorCode } =
        useContext(EntryContext);

    const query = useQuery();
    const data = query.get('data');
    const debug_prizeType = query.get('debug_prizeType') || null;
    const debug_NumberOfScans = query.get('debug_NumberOfScans') || null;

    // Context error will trigger the local error message
    useEffect(() => {
        if (errorCode) {
            setErrorMessage(errorCode);
            setEntry(null);
        }
    }, [errorCode, setEntry]);

    useEffect(() => {
        async function fetchData() {
            if (data?.length && !hasApiBeenCalled.current) {
                hasApiBeenCalled.current = true;
                const ticketData = await checkTicketData(data);
                if (ticketData) {
                    await getIGRAuthToken(data);
                    await getCMSData(data, ticketData);
                }
            }
        }

        if (!data) {
            //? ERROR_NO_TICKET_DATA_PARAM: Initial screen without any data parameter
            setErrorMessage(ERROR_MESSAGES.NO_TICKET_DATA_PARAM);
            return;
        }

        fetchData();
    }, [data]);

    // Navigate to the game page when all data is available
    useEffect(() => {
        if (!errorCode && game?.game_id && revealGames?.length && siteAssets?.name && igrAuthToken?.credentials && entry?.TicketDetails?.ValidationNumber) {
            navigate('/age-verification', { replace: true });
        }
    }, [game, revealGames, siteAssets, igrAuthToken?.credentials, entry, errorCode, data, navigate]);

    // Check ticket data is used to validate the ticket number and fetch entry details
    const checkTicketData = async (data) => {
        const payload = {
            ticket_number: data,
            entry_method: 'scan',
        };

        try {
            const response = await API.post('scratchconnect/enter', payload);

            if (response?.data) {
                let entryResponse = response?.data;

                // If debug mode is enabled, override the prize type
                if (debug_prizeType) {
                    entryResponse.TicketDetails.OriginalPrizeType = entryResponse.TicketDetails.PrizeType;
                    entryResponse.TicketDetails.PrizeType = debug_prizeType;
                    console.log(`🔧 Debug Prize Type: ${entryResponse.TicketDetails.OriginalPrizeType} >> ${entryResponse.TicketDetails.PrizeType}`);
                }

                // If debug mode is enabled, override the number of scans and clear the reveal game id to allow retesting
                if (debug_NumberOfScans) {
                    entryResponse.TicketDetails.OriginalNumberOfScans = entryResponse.TicketDetails.NumberOfScans;
                    entryResponse.TicketDetails.NumberOfScans = parseInt(debug_NumberOfScans);
                    console.log(
                        `🔧 Debug Number of Scans: ${entryResponse.TicketDetails.OriginalNumberOfScans} >> ${entryResponse.TicketDetails.NumberOfScans}`
                    );

                    entryResponse.TicketDetails.OriginalRevealGameId = entryResponse.TicketDetails.RevealGameId;
                    entryResponse.TicketDetails.RevealGameId = null;
                    console.log(`🔧 Debug Reveal Game ID: ${entryResponse.TicketDetails.OriginalRevealGameId} >> ${entryResponse.TicketDetails.RevealGameId}`);
                }

                setEntry(entryResponse);
                //? For debug: console.log('🎫 Ticket Data:', response?.data)   ;
                return entryResponse;
            } else {
                //? ERROR_INVALID_TICKET_DATA: Wrong ticket hash. The ticket check API call has no data object in the response
                setErrorMessage(ERROR_MESSAGES.INVALID_TICKET_DATA);
                return null;
            }
        } catch (error) {
            if (error?.response?.data?.code === '_INVALID_TICKET') {
                //? ERROR_INVALID_TICKET_DATA: Wrong ticket hash. The ticket check API call has no data object in the response
                setErrorMessage(ERROR_MESSAGES.INVALID_TICKET_DATA);
                return null;
            } else {
                //? ERROR_INVALID_TICKET_ENTER_RESPONSE: The scratchconnect/enter API call has returned with 550 _INVALID_TICKET error
                setErrorMessage(ERROR_MESSAGES.INVALID_TICKET_ENTER_RESPONSE);
                return null;
            }
        }
    };

    // IGR Auth Token needed for animation, token is used to fetch the animation data
    const getIGRAuthToken = async (data) => {
        try {
            const response = await API.get(`credentials/reveal?data=${data}`);

            if (response.data?.credentials) {
                setIgrAuthToken(response.data);
            } else {
                //? ERROR_IGR_NO_AUTH_TOKEN: No token found in the credentials/reveal response
                setErrorMessage(ERROR_MESSAGES.IGR_NO_AUTH_TOKEN);
                return false;
            }
            return true;
        } catch (error) {
            //? ERROR_IGR_AUTH_TOKEN_FAILURE: the credentials/reveal response could not get the token due to IGR or network error (500)
            setErrorMessage(ERROR_MESSAGES.IGR_AUTH_TOKEN_FAILURE);
            console.error('🔴 API Error: getIGRAuthToken', error);
            return false;
        }
    };

    // Setting game, reveal game and site asset configuration with CMS data
    const getCMSData = async (data, entryData) => {
        try {
            const response = await API.get(`cms?data=${data}`);

            let cmsData = response?.data?.data ?? {};

            const cmsVersion = cmsData?.version ?? 'v1.0';
            setCmsVersion(cmsVersion);

            let currentGame = null;
            if (cmsData?.scratchConnect?.scratchConnectGames) {
                currentGame = cmsData?.scratchConnect?.scratchConnectGames.filter(
                    (game) => parseInt(game.game_id) === parseInt(entryData?.TicketDetails?.GameId)
                )?.[0];
                if (currentGame) {
                    setGame(currentGame);
                } else {
                    //? ERROR_CMS_NO_GAME_DATA: The CMS data has no scratchConnect > scratchConnectGames with the game id
                    setErrorMessage(ERROR_MESSAGES.CMS_NO_GAME_DATA);
                    return false;
                }
            } else {
                //? ERROR_CMS_NO_GAME_DATA: The CMS data has no scratchConnect > scratchConnectGames object
                setErrorMessage(ERROR_MESSAGES.CMS_NO_GAME_DATA);
                return false;
            }

            if (cmsData?.reveal?.revealGames) {
                const associatedRevealGames = JSON.parse(currentGame?.associatedRevealGames ?? []);

                if (!Array.isArray(associatedRevealGames) || !associatedRevealGames.length) {
                    //? ERROR_CMS_NO_ASSOCIATED_REVEAL_GAME: The CMS data has no scratchConnect > revealGames object with reveal id associated with no scratchConnect > scratchConnectGames object reveal_id
                    setErrorMessage(ERROR_MESSAGES.CMS_NO_ASSOCIATED_REVEAL_GAME);
                } else {
                    const filteredReveals = cmsData?.reveal?.revealGames.filter((revealGame) => associatedRevealGames?.includes(revealGame.game_id));
                    if (filteredReveals.length) {
                        setRevealGames(filteredReveals);
                    } else {
                        //? ERROR_CMS_NO_ASSOCIATED_REVEAL_GAME: The CMS data has no scratchConnect > revealGames object with reveal id associated with no scratchConnect > scratchConnectGames object reveal_id
                        setErrorMessage(ERROR_MESSAGES.CMS_NO_ASSOCIATED_REVEAL_GAME);
                        return false;
                    }
                }
            } else {
                //? ERROR_CMS_NO_REVEAL_GAME_DATA: The CMS data has no scratchConnect has no revealGames object
                setErrorMessage(ERROR_MESSAGES.CMS_NO_REVEAL_GAME_DATA);
                return false;
            }

            // CMS data migration: v1.1
            if (cmsVersion === 'v1.1') {
                if (currentGame?.siteAssets) {
                    cmsData.siteAssets = currentGame.siteAssets;
                }
            }

            if (cmsData?.siteAssets) {
                setSiteAssets(cmsData?.siteAssets);
            } else {
                //? ERROR_CMS_NO_SITE_ASSETS: The CMS data has no scratchConnect > site assets object
                setErrorMessage(ERROR_MESSAGES.CMS_NO_SITE_ASSETS);
                return false;
            }

            return true;
        } catch (error) {
            //? ERROR_CMS_NO_CREDENTIALS: Failed to retrieve CMS data from the api/cms endpoint what could caused by credentials issue
            setErrorMessage(ERROR_MESSAGES.CMS_NO_CREDENTIALS);
            console.error('🔴 API Error: getCMSCredentials', error);
            return false;
        }
    };

    // Display error message if it exists
    if (errorMessage) {
        return <ErrorMessage errorMessage={errorCode ?? errorMessage} />;
    }

    return (
        <>
            <Helmet>
                <title>Welcome - Scratch Connect</title>
            </Helmet>

            <DisplayContent note='Loading' isVisible={data}>
                <div className='loading-indicator'>
                    <div className='d-flex justify-content-center bg-white'>
                        <div className='loading-indicator-spinner' role='status'></div>
                    </div>
                </div>
            </DisplayContent>
        </>
    );
};

export default Home;
